import { Spinner } from "@/components/ui/spinner";
import { intentToSolidClassName } from "@/components/ui/utils";
import { useTabs, TabMetadata } from "@/contexts/tabsContext";
import clsx from "clsx";
import { FC, useState, useEffect } from "react";

export type TabTitleProps = {
  id: string
}

/**
 * Tab title component that subscribes to ID-scoped metadata events.
 * Only re-renders when THIS tab's metadata changes (not when other tabs change).
 */
export const TabTitle: FC<TabTitleProps> = ({ id }) => {
  const { metadataEvents, getTabMetadata } = useTabs();
  
  // Initialize from cache (synchronous)
  const [meta, setMeta] = useState<TabMetadata | undefined>(() => getTabMetadata(id));
  
  // Subscribe to updates for THIS tab only
  useEffect(() => {
    const handler = (newMeta: TabMetadata) => setMeta(newMeta);
    metadataEvents.subscribe(id, "updated", handler);
    return () => metadataEvents.unsubscribe(id, "updated", handler);
  }, [id, metadataEvents]);
  
  // Loading state
  if (!meta) {
    return (
      <div className="flex items-center gap-1.5 whitespace-nowrap">
        <Spinner size="16px" /> Loading...
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap">
      {meta.icon}
      <span className="truncate">{meta.title}</span>
      {meta.dotIntent && (
        <span className={clsx("w-2 h-2 rounded-full flex-shrink-0", intentToSolidClassName(meta.dotIntent))} />
      )}
    </div>
  );
}